/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {string} The key that maps viewed product ids to handles. */
  static #HANDLES_STORAGE_KEY = 'viewedProductHandles';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 12;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {string} [productHandle] - The handle used for storefront product requests.
   */
  static addProduct(productId, productHandle) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));

    if (productHandle) {
      let viewedProductHandles = this.getProductHandles();
      viewedProductHandles = viewedProductHandles.filter((product) => product.id !== productId);
      viewedProductHandles.unshift({ id: productId, handle: productHandle });
      viewedProductHandles = viewedProductHandles.slice(0, this.#MAX_PRODUCTS);
      localStorage.setItem(this.#HANDLES_STORAGE_KEY, JSON.stringify(viewedProductHandles));
    }
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
    localStorage.removeItem(this.#HANDLES_STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }

  /**
   * Retrieves recently viewed product handles while preserving the visit order.
   * @returns {{id: string, handle: string}[]} Product identifiers and handles.
   */
  static getProductHandles() {
    try {
      const products = JSON.parse(localStorage.getItem(this.#HANDLES_STORAGE_KEY) || '[]');
      return Array.isArray(products)
        ? products.filter((product) => product && typeof product.id === 'string' && typeof product.handle === 'string')
        : [];
    } catch {
      return [];
    }
  }
}
